<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { useStorage } from '@vueuse/core'
import { useDisplay } from 'vuetify'
import ReportPreviewPanel from '@/components/createdUi/ReportPreviewPanel.vue'
import { getApiError } from '@/composables/getApiError'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import type { ReportFormat, ReportPreview } from '@/modules/reportTemplates/api'
import { createPreviewScheduler } from '@/modules/reportTemplates/preview'
import { reportBrandingApi } from '@/modules/settings/services/reportBrandingApi'
import type { BrandingConfig, BrandingProfileKey, BrandingReference, BrandingSample, BrandingState } from '@/modules/settings/services/reportBrandingApi'

const props = defineProps<{ active: boolean }>()
defineEmits<{ organization: [] }>()
const feedback = useFeedbackStore()
const { smAndDown } = useDisplay()
const state = ref<BrandingState | null>(null)
const config = ref<BrandingConfig | null>(null)
const selected = ref<BrandingProfileKey>('report')
const docFormat = ref<'pdf' | 'docx'>('pdf')
const format = computed<ReportFormat>(() => selected.value === 'spreadsheet' ? 'xlsx' : docFormat.value)
const profile = computed(() => config.value?.[selected.value])
const loading = ref(false)
const busy = ref(false)
const downloading = ref(false)
const error = ref('')
const preview = ref<ReportPreview | null>(null)
const previewError = ref('')
const previewLoading = ref(false)
const previewVisible = useStorage('branding-preview-visible', true)
const mobileTab = ref('settings')
const confirmAction = ref<'publish' | 'restore' | 'reload' | null>(null)
const restoreId = ref<number | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const dirty = computed(() => !!state.value && JSON.stringify(config.value) !== JSON.stringify(state.value.draft))
const profiles: Array<{ key: BrandingProfileKey; title: string; text: string; icon: string }> = [
  { key: 'official', title: 'Официальный бланк', text: 'Заявления, приказы, справки', icon: 'ri-file-text-line' },
  { key: 'report', title: 'Внутренние отчёты', text: 'НС, аудиты, обращения, заявки', icon: 'ri-file-chart-line' },
  { key: 'spreadsheet', title: 'Excel', text: 'Таблицы и печать реестров', icon: 'ri-file-excel-line' },
]
const margins = [
  { key: 'margin_top', label: 'Верхнее', min: 10 }, { key: 'margin_bottom', label: 'Нижнее', min: 15 },
  { key: 'margin_left', label: 'Левое', min: 10 }, { key: 'margin_right', label: 'Правое', min: 10 },
] as const
const colors = [{ key: 'primary', label: 'Заголовки и шапка таблиц' }, { key: 'accent', label: 'Акцент' }, { key: 'pale', label: 'Фон разделов' }] as const
const valid = computed(() => config.value && profiles.every(({ key }) => {
  const p = config.value![key]
  return colors.every(color => /^#[0-9a-f]{6}$/i.test(p[color.key]))
    && margins.every(m => Number.isInteger(p[m.key]) && p[m.key] >= m.min && p[m.key] <= 30)
    && p.footer.length <= 120 && !/[\x00-\x1f\x7f]/.test(p.footer)
}))
const canPreview = computed(() => props.active && previewVisible.value && (!smAndDown.value || mobileTab.value === 'preview'))
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value))

function accept(result: BrandingState) {
  state.value = result
  config.value = clone(result.draft)
}
async function load() {
  loading.value = true
  error.value = ''
  try { accept(await reportBrandingApi.get()) }
  catch (caught) { error.value = getApiError(caught, 'Не удалось загрузить оформление') }
  finally { loading.value = false }
}
const scheduler = createPreviewScheduler<BrandingSample, ReportPreview>({
  load: reportBrandingApi.preview,
  pending: () => { previewLoading.value = true; previewError.value = '' },
  resolved: result => { preview.value = result; previewLoading.value = false },
  rejected: caught => { previewError.value = getApiError(caught, 'Не удалось обновить образец'); previewLoading.value = false },
})
function updatePreview() {
  if (config.value && canPreview.value && valid.value) {
    scheduler.schedule({ config: clone(config.value), profile: selected.value, format: format.value })
  } else {
    scheduler.cancel()
    preview.value = null
    previewLoading.value = false
    previewError.value = config.value && !valid.value ? 'Проверьте цвета, поля страницы и текст колонтитула.' : ''
  }
}
watch([config, selected, format, canPreview], updatePreview, { deep: true })

async function save(publish = false) {
  if (!config.value || !state.value || !valid.value) return
  busy.value = true
  error.value = ''
  try {
    const result = await (publish ? reportBrandingApi.publish : reportBrandingApi.save)(clone(config.value), state.value.revision)
    accept(result)
    feedback.showToast({ type: 'success', message: publish ? 'Оформление опубликовано для новых документов' : 'Черновик оформления сохранён' })
  } catch (caught) { error.value = getApiError(caught, 'Не удалось сохранить оформление') }
  finally { busy.value = false; confirmAction.value = null }
}
async function confirm() {
  if (confirmAction.value === 'publish') return save(true)
  if (confirmAction.value === 'reload') { confirmAction.value = null; return load() }
  if (!state.value || restoreId.value === null) return
  busy.value = true
  error.value = ''
  try {
    accept(await reportBrandingApi.restore(restoreId.value, state.value.revision))
    feedback.showToast({ type: 'success', message: 'Прежнее оформление опубликовано новой версией' })
  } catch (caught) { error.value = getApiError(caught, 'Не удалось вернуть версию') }
  finally { busy.value = false; confirmAction.value = null }
}
function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
async function downloadSample() {
  if (!config.value) return
  downloading.value = true
  try {
    downloadBlob(await reportBrandingApi.download({ config: clone(config.value), profile: selected.value, format: format.value }), `Образец оформления.${format.value}`)
  } catch (caught) { error.value = getApiError(caught, 'Не удалось скачать образец') }
  finally { downloading.value = false }
}
async function upload(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  if (!/\.(pdf|docx)$/i.test(file.name) || file.size > 5 * 1024 * 1024) {
    error.value = 'Выберите PDF или DOCX размером до 5 МБ.'
    return
  }
  busy.value = true
  error.value = ''
  try {
    const result = await reportBrandingApi.upload(file)
    if (state.value) state.value.references = result.references
    feedback.showToast({ type: 'success', message: 'Образец сохранён' })
  } catch (caught) { error.value = getApiError(caught, 'Не удалось загрузить образец') }
  finally { busy.value = false }
}
async function referenceAction(reference: BrandingReference, remove = false) {
  busy.value = true
  error.value = ''
  try {
    if (remove) {
      await reportBrandingApi.removeReference(reference.id)
      if (state.value) state.value.references = state.value.references.filter(item => item.id !== reference.id)
    } else downloadBlob(await reportBrandingApi.reference(reference.id), reference.name)
  } catch (caught) { error.value = getApiError(caught, 'Не удалось обработать образец') }
  finally { busy.value = false }
}
function beforeUnload(event: BeforeUnloadEvent) {
  if (dirty.value) { event.preventDefault(); event.returnValue = '' }
}
onBeforeRouteLeave(() => !dirty.value || window.confirm('В оформлении есть несохранённые изменения. Покинуть страницу?'))
onMounted(() => { load(); window.addEventListener('beforeunload', beforeUnload) })
onBeforeUnmount(() => { scheduler.cancel(); window.removeEventListener('beforeunload', beforeUnload) })
</script>

<template>
  <section class="branding-panel pa-4 pa-md-6">
    <div class="d-flex align-start justify-space-between flex-wrap gap-3 mb-4">
      <div>
        <h2 class="text-h6">Фирменное оформление документов</h2>
        <p class="text-body-2 text-medium-emphasis mt-1 mb-0">Единый стиль для форм и отчётов портала. Слева — настройки, справа — учебный образец.</p>
      </div>
      <VChip size="small" :color="state?.published_id ? 'success' : 'secondary'" variant="tonal">
        {{ state?.published_id ? `Опубликована версия ${state.published_id}` : 'Стандартное оформление' }}
      </VChip>
    </div>
    <VAlert v-if="error" type="error" variant="tonal" class="mb-4" closable @click:close="error = ''">
      {{ error }}
      <VBtn variant="text" size="small" :disabled="busy" @click="dirty ? confirmAction = 'reload' : load()">Обновить настройки</VBtn>
    </VAlert>
    <VProgressLinear v-if="loading" indeterminate class="mb-4" />
    <template v-if="config && state && profile">
      <div class="branding-profiles mb-4">
        <button v-for="item in profiles" :key="item.key" type="button" class="branding-profile" :class="{ selected: selected === item.key }" :aria-pressed="selected === item.key" @click="selected = item.key">
          <VIcon :icon="item.icon" size="24" />
          <span><strong>{{ item.title }}</strong><small>{{ item.text }}</small></span>
          <VIcon v-if="config[item.key].enabled" icon="ri-checkbox-circle-line" size="18" color="primary" />
        </button>
      </div>
      <div class="d-flex align-center justify-space-between flex-wrap gap-2 mb-3">
        <span class="text-caption text-medium-emphasis">{{ dirty ? 'Есть несохранённые изменения' : 'Черновик сохранён' }} · Публикация применится ко всем новым выгрузкам.</span>
        <VBtn variant="text" size="small" :prepend-icon="previewVisible ? 'ri-eye-off-line' : 'ri-eye-line'" :aria-expanded="previewVisible" @click="previewVisible = !previewVisible">
          {{ previewVisible ? 'Скрыть предпросмотр' : 'Показать предпросмотр' }}
        </VBtn>
      </div>
      <VTabs v-if="smAndDown && previewVisible" v-model="mobileTab" class="mb-3">
        <VTab value="settings">Настройки</VTab><VTab value="preview">Предпросмотр</VTab>
      </VTabs>
      <div class="branding-workspace" :class="{ 'without-preview': !previewVisible }">
        <div v-show="!smAndDown || !previewVisible || mobileTab === 'settings'" class="branding-form">
          <fieldset :disabled="busy || loading" class="branding-fieldset">
            <VSwitch v-model="profile.enabled" label="Использовать фирменный профиль" color="primary" hide-details />
            <p class="text-caption text-medium-emphasis mb-4">{{ profile.enabled ? 'После публикации профиль будет применяться к соответствующим формам.' : 'Для этих форм используется стандартное оформление.' }}</p>
            <div class="branding-settings" :class="{ 'is-disabled': !profile.enabled }">
              <VSelect v-model="profile.font" :disabled="!profile.enabled" :items="[{ title: 'Inter', value: 'inter' }, { title: 'DejaVu Sans', value: 'dejavu' }]" label="Шрифт" class="mb-4" />
              <template v-if="selected !== 'spreadsheet'">
                <VCheckbox v-model="profile.show_logo" :disabled="!profile.enabled" label="Логотип организации в шапке" hide-details />
                <VCheckbox v-model="profile.show_details" :disabled="!profile.enabled" label="Адрес, контакты и реквизиты в шапке" hide-details class="mb-2" />
              </template>
              <p class="text-caption text-medium-emphasis mb-2">Реквизиты и логотип берутся из сохранённых сведений об организации. Публикация фиксирует их в версии оформления.</p>
              <VBtn variant="text" size="small" prepend-icon="ri-hospital-line" class="mb-4" @click="$emit('organization')">Открыть сведения организации</VBtn>
              <VAlert v-if="selected === 'official'" type="info" variant="tonal" density="compact" class="mb-4">Заявления и приказы сохраняют строгий чёрно-белый стиль.</VAlert>
              <template v-else>
                <div v-for="color in colors.filter(item => selected !== 'spreadsheet' || item.key === 'primary')" :key="color.key" class="branding-color mb-3">
                  <input v-model="profile[color.key]" type="color" :aria-label="`Выбрать цвет: ${color.label}`" :disabled="!profile.enabled">
                  <VTextField v-model="profile[color.key]" :label="color.label" :disabled="!profile.enabled" placeholder="#24506A" maxlength="7" :rules="[value => /^#[0-9a-f]{6}$/i.test(value) || 'Укажите цвет в формате #24506A']" />
                </div>
              </template>
              <h3 class="text-subtitle-2 mt-5 mb-3">Поля страницы, мм{{ selected === 'spreadsheet' ? ' · при печати' : '' }}</h3>
              <div class="branding-margins mb-4">
                <VTextField v-for="margin in margins" :key="margin.key" v-model.number="profile[margin.key]" :label="margin.label" type="number" :min="margin.min" :max="30" :disabled="!profile.enabled" :rules="[value => Number.isInteger(Number(value)) && Number(value) >= margin.min && Number(value) <= 30 || `От ${margin.min} до 30 мм`]" />
              </div>
              <VTextField v-model="profile.footer" label="Текст внизу страницы" hint="Например: Служба качества медицинской организации" persistent-hint maxlength="120" counter="120" :disabled="!profile.enabled" />
              <p v-if="selected === 'spreadsheet'" class="text-caption text-medium-emphasis mt-2">В Excel настраиваются шрифт, шапка таблицы и поля печати. Организация и нижний колонтитул видны при печати.</p>
            </div>
          </fieldset>
        </div>
        <div v-if="previewVisible" v-show="!smAndDown || mobileTab === 'preview'" class="branding-preview">
          <div class="d-flex align-center justify-space-between flex-wrap gap-2 pa-3">
            <VSelect v-if="selected !== 'spreadsheet'" v-model="docFormat" :items="[{ title: 'PDF', value: 'pdf' }, { title: 'Word', value: 'docx' }]" label="Формат" hide-details density="compact" style="max-inline-size: 160px" />
            <span v-else class="text-subtitle-2">Образец реестра Excel</span>
            <VBtn variant="text" size="small" prepend-icon="ri-download-2-line" :disabled="!valid" :loading="downloading" @click="downloadSample">Скачать образец</VBtn>
          </div>
          <ReportPreviewPanel :preview="preview" :loading="previewLoading" :error="previewError" :format="format" @retry="updatePreview" />
        </div>
      </div>
      <div class="branding-actions d-flex flex-wrap gap-3 mt-4 mb-6">
        <VBtn variant="outlined" prepend-icon="ri-save-line" :disabled="!valid || !dirty || busy" @click="save()">Сохранить черновик</VBtn>
        <VBtn prepend-icon="ri-check-line" :disabled="!valid || busy" @click="confirmAction = 'publish'">Опубликовать оформление</VBtn>
        <VProgressCircular v-if="busy" indeterminate size="22" />
      </div>
      <VExpansionPanels variant="accordion">
        <VExpansionPanel title="Готовый фирменный бланк и особые требования">
          <VExpansionPanelText>
            <p class="text-body-2">Загрузите действующий бланк, пример документа или брендбук. Образцы сохраняются для ручной адаптации: загрузка сама по себе не переносит вёрстку и не меняет формы.</p>
            <VTextarea v-model="config.adaptation_notes" :disabled="busy" label="Что нужно сохранить или изменить" hint="Например: расположение реквизитов, подписи, особая структура утверждённой формы. Сохраните текст кнопкой «Сохранить черновик»." persistent-hint maxlength="3000" counter="3000" rows="3" class="mb-4" />
            <VBtn variant="outlined" prepend-icon="ri-upload-2-line" :disabled="busy || state.references.length >= 5" @click="fileInput?.click()">Загрузить образец</VBtn>
            <span class="text-caption text-medium-emphasis ms-3">PDF или DOCX, до 5 МБ · до 5 файлов</span>
            <input ref="fileInput" type="file" accept=".pdf,.docx" class="d-none" @change="upload">
            <div v-for="item in state.references" :key="item.id" class="d-flex align-center justify-space-between gap-2 mt-3">
              <span class="text-body-2 text-break">{{ item.name }} · {{ Math.ceil(item.size / 1024) }} КБ</span>
              <div class="d-flex">
                <VBtn icon="ri-download-2-line" variant="text" size="small" :aria-label="`Скачать ${item.name}`" :disabled="busy" @click="referenceAction(item)" />
                <VBtn icon="ri-delete-bin-line" variant="text" size="small" :aria-label="`Удалить ${item.name}`" :disabled="busy" @click="referenceAction(item, true)" />
              </div>
            </div>
            <p class="text-caption text-medium-emphasis mt-3 mb-0">Файлы доступны только пользователям с правом изменения настроек портала. Образцы и описание сохраняются здесь; заявка на адаптацию автоматически не отправляется.</p>
          </VExpansionPanelText>
        </VExpansionPanel>
        <VExpansionPanel title="История публикаций">
          <VExpansionPanelText>
            <p v-if="!state.versions.length" class="text-body-2">Публикаций пока нет. Сначала настройте и проверьте образцы.</p>
            <p v-else class="text-caption text-medium-emphasis">Последние 100 публикаций. Возврат создаёт новую версию и заменяет текущий черновик.</p>
            <div v-for="version in state.versions" :key="version.id" class="branding-version">
              <div><strong>Версия {{ version.id }}</strong><span v-if="version.id === state.published_id"> · Действует</span><span v-if="version.restored_from"> · Возврат к версии {{ version.restored_from }}</span>
                <div class="text-caption text-medium-emphasis">{{ new Date(version.created_at).toLocaleString('ru-RU') }} · {{ version.created_by || 'Администратор' }}</div>
              </div>
              <VBtn v-if="version.id !== state.published_id" size="small" variant="text" :disabled="busy" @click="restoreId = version.id; confirmAction = 'restore'">Вернуть версию</VBtn>
            </div>
          </VExpansionPanelText>
        </VExpansionPanel>
      </VExpansionPanels>
    </template>
    <VDialog :model-value="!!confirmAction" max-width="500" :persistent="busy" @update:model-value="value => { if (!value && !busy) confirmAction = null }">
      <VCard :title="confirmAction === 'publish' ? 'Опубликовать оформление?' : confirmAction === 'restore' ? `Вернуть версию ${restoreId}?` : 'Обновить настройки?'">
        <VCardText>{{ confirmAction === 'publish' ? 'Все новые формы и отчёты соответствующих профилей будут использовать это оформление и сохранённые сейчас реквизиты организации. Ранее скачанные файлы не изменятся.' : confirmAction === 'restore' ? 'Оформление и реквизиты выбранной версии будут опубликованы заново. Текущий черновик будет заменён.' : 'Несохранённые изменения будут заменены последним сохранённым черновиком.' }}</VCardText>
        <VCardActions><VSpacer /><VBtn :disabled="busy" @click="confirmAction = null">Отмена</VBtn><VBtn color="primary" :loading="busy" @click="confirm">{{ confirmAction === 'publish' ? 'Опубликовать' : confirmAction === 'restore' ? 'Вернуть' : 'Обновить' }}</VBtn></VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>

<style scoped>
.branding-profiles { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.branding-profile { display: flex; align-items: center; gap: 12px; padding: 16px; border: 1px solid rgba(var(--v-theme-on-surface), .14); border-radius: 8px; text-align: start; color: inherit; }
.branding-profile.selected { border-color: rgb(var(--v-theme-primary)); background: rgba(var(--v-theme-primary), .06); }
.branding-profile:focus-visible { outline: 2px solid rgb(var(--v-theme-primary)); outline-offset: 2px; }
.branding-profile strong,.branding-profile small { display: block; }.branding-profile small { margin-block-start: 4px; opacity: .7; }
.branding-workspace { display: grid; grid-template-columns: minmax(300px, .8fr) minmax(0, 1.2fr); gap: 24px; align-items: start; }
.branding-workspace.without-preview { grid-template-columns: minmax(0, 760px); }
.branding-fieldset { border: 0; min-inline-size: 0; padding: 0; margin: 0; }
.branding-color { display: flex; align-items: start; gap: 10px; }.branding-color input { inline-size: 44px; block-size: 42px; padding: 3px; border: 1px solid #ccc; border-radius: 6px; }
.branding-margins { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.branding-preview { position: sticky; inset-block-start: 80px; block-size: min(850px, 85vh); min-block-size: 500px; display: flex; flex-direction: column; overflow: hidden; border: 1px solid rgba(var(--v-theme-on-surface), .12); border-radius: 8px; }
.branding-preview > :first-child { flex: 0 0 auto; }.branding-preview > :last-child { flex: 1 1 0; min-block-size: 0; }
.branding-version { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; padding-block: 12px; border-block-end: 1px solid rgba(var(--v-theme-on-surface), .08); }
.is-disabled { opacity: .65; }
@media(max-width:959px) { .branding-profiles,.branding-workspace { grid-template-columns: minmax(0, 1fr); }.branding-profile { padding: 12px; }.branding-preview { position: static; block-size: 70dvh; min-block-size: 420px; } }
</style>
